import { NIGHTMARES } from '@somnhold/content';
import { cellAtWorld } from '../grid.ts';
import { idiv, imax, iatan2, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, SendaNpc } from '../types.ts';
import type { BoatRules } from '../bote-types.ts';
import { ev, spawnEnemy } from './combat.ts';

/**
 * What the three acts of the Long Hunt's chapter 3 share (docs/sendas/barbaro.md, «El bote»): Bera's long boat, its hull
 * a list of cells, the boat itself a thing to keep (the acts' `boat` npc, a prop: its life in blows, 40). A wolf aboard
 * that isn't near the hero gnaws at the hull: one wolf in three does, a blow every `gnaw`, until the hero comes.
 *
 * Cost: the hull's cells sorted (a binary search); the gnawers looked at once a second.
 */

/** Whether a cell is the hull's. */
export const onHull = (b: BoatRules, cell: number): boolean => {
  let lo = 0;
  let hi = b.cells.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const c = b.cells[mid] as number;
    if (c === cell) return true;
    if (c < cell) lo = mid + 1;
    else hi = mid - 1;
  }
  return false;
};

export const boatNpc = (s: BattleState): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === 'boat');

/** Blows on the boat (the hull crushed, gnawed, leapt on). */
export const hitBoat = (s: BattleState, n: number, x: number, y: number): void => {
  const b = boatNpc(s);
  if (!b || b.hp <= 0 || n <= 0) return;
  b.hp = imax(0, b.hp - n);
  ev(s, { t: 'senda', kind: 'hit', npc: 'boat', x, y });
  ev(s, { t: 'senda', kind: 'bGnaw', npc: 'boat', x, y, n });
  if (b.hp <= 0) ev(s, { t: 'senda', kind: 'down', npc: 'boat', x: b.x, y: b.y });
};

/** Within reach of a shield on the gunwale. */
export const heldAt = (b: BoatRules, x: number, y: number, hold: number): boolean => b.shields.some((p) => withinRange(p.x, p.y, x, y, hold));

/** Whether this wolf is one that gnaws (one in three of the pack). */
export const gnawer = (e: Enemy): boolean => e.kind === 'wolf' && e.id % 3 === 0;

/**
 * A gnawer aboard away from the hero stands at the hull and gnaws (true: the act moved it); its blows counted by
 * `gnawTick`.
 */
export const gnawHold = (s: BattleState, b: BoatRules, e: Enemy): boolean => {
  if (!gnawer(e) || !onHull(b, cellAtWorld(s, e.x, e.y))) return false;
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (h && withinRange(h.x, h.y, e.x, e.y, b.gnawReach)) return false;
  // Down at the planks, facing out over the gunwale.
  e.facing = iatan2(0, e.x >= 0 ? 1 : -1);
  if (e.anim !== 'attack' || e.animT > 10) {
    e.anim = 'attack';
    e.animT = 0;
  }
  return true;
};

/** The gnawers' blows: one each, every `gnaw` (returns the countdown's next value). */
export const gnawTick = (s: BattleState, b: BoatRules, cd: number): number => {
  if (cd > 0) return cd - 1;
  const h = s.heroes.find((x) => x.downTicks === 0);
  let n = 0;
  let ax = 0;
  let ay = 0;
  for (const e of s.enemies) {
    if (e.hp <= 0 || !gnawer(e) || !onHull(b, cellAtWorld(s, e.x, e.y))) continue;
    if (h && withinRange(h.x, h.y, e.x, e.y, b.gnawReach)) continue;
    n++;
    ax = e.x;
    ay = e.y;
  }
  hitBoat(s, n, ax, ay);
  return b.gnaw;
};

/** A wolf climbing aboard at an oar's rowlock (from the sea at its side). */
export const climbAt = (s: BattleState, b: BoatRules, k: number, hp: number): Enemy => {
  const p = b.oars[k] as { x: number; y: number };
  const out = p.x >= 0 ? 1 : -1;
  return spawnEnemy(s, 'wolf', 1, false, 0, p.x + out * 300, p.y + ((k * 137) % 300) - 150, hp);
};

/** The gunwale's point nearest a spot off the boat (where one comes aboard from it). */
export const gunwaleNear = (b: BoatRules, x: number, y: number): { x: number; y: number } => {
  const yy = imax(b.bow + 900, y < b.stern - 900 ? y : b.stern - 900);
  const half = Math.abs(yy) > 3000 ? 450 : 900;
  return { x: x >= 0 ? half : -half, y: yy };
};

/** Soot crows off the sea (they fly: no hull for them). */
export const crowsAt = (s: BattleState, n: number, x: number, y: number, hp: number): void => {
  for (let k = 0; k < n; k++) spawnEnemy(s, 'crow', 1, false, 0, x + k * 500, y - k * 400, imax(1, hp >> 1));
};

/** Whether a nightmare is one the waves and the hull's rules move (not the flying ones). */
export const onDeck = (e: Enemy): boolean => e.hp > 0 && !NIGHTMARES[e.kind].flying;

/** Half the way from a to b (for the drawing's anchors). */
export const halfway = (a: { x: number; y: number }, b: { x: number; y: number }): { x: number; y: number } => ({ x: a.x + idiv(b.x - a.x, 2), y: a.y + idiv(b.y - a.y, 2) });
